'use client';

import { useAuthProvider } from '@modules/auth/providers/AuthProvider/AuthProvider';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { useUser } from 'app/_providers/User';
import useFetcher from 'app/_utils/data/useFetcher';

import React from 'react';
import { AccountState, Organization } from 'shared/src/generated/openapi/v2/types';
import useSWR, { KeyedMutator } from 'swr';

export type IOrganizationContext = Organization;
const OrganizationContext = React.createContext<IOrganizationContext & { mutate: KeyedMutator<Organization> }>(
  {} as IOrganizationContext & { mutate: KeyedMutator<Organization> });

const defaultContext: Organization = {
  accountId: '',
  created: '',
  updated: '',
  state: undefined as unknown as AccountState,
  organizationId: '',
  organizationAlias: '',
  license: undefined,
  region: '',
  maxWorkspaces: 0,
  customGroupMapping: {
    orgOwner: [],
    orgAdmin: [],
    orgUser: [],
  },
  capabilities: {
    'cloud.iam.teams': { enabled: false },
    'cloud.workspace.releaseChannels': { enabled: false },
    'cloud.workspace.provisioning': { enabled: false },
    'cloud.iam.eventlogs': { enabled: false },
    'cloud.workspace.multiRegion': { enabled: false },
  },
};

export const OrganizationProvider = ({ children }: { children: React.ReactNode }) => {
  const { authorizedOrganizationId } = useAuthProvider();
  const { memberships } = useUser();
  const [organization, setOrganization] = React.useState<IOrganizationContext | undefined>(undefined);
  const { user, isLoading } = useCommonAuthProvider();
  const { fetcher } = useFetcher();
  const { data, mutate } = useSWR<Organization>(
    authorizedOrganizationId ? `${process.env.ZEUS_API_URL}/v1/tenants/${authorizedOrganizationId}/organization` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  React.useEffect(() => {
    if (isLoading) {
      return;
    }

    const currentAuthorizedMembership = memberships?.data?.find(
      (membership) => membership.organizationId === authorizedOrganizationId)
      ;

    if (currentAuthorizedMembership?.organization || data) {
      setOrganization({
        ...data,
        organizationId: currentAuthorizedMembership?.organizationId || data?.organizationId || '',
        organizationAlias: currentAuthorizedMembership?.organization.organizationAlias || data?.organizationAlias,
      } as IOrganizationContext);
    } else {
      // user is not logged into an organization
      setOrganization(undefined);
    }
  }, [user, isLoading, data]);

  return (
    <OrganizationContext.Provider
      value={{
        ...defaultContext,
        ...(organization || {}),
        mutate,
      } as IOrganizationContext & { mutate: KeyedMutator<Organization> }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export function useOrganization(): IOrganizationContext {
  const context = React.useContext(OrganizationContext);

  if (context === undefined) {
    throw new Error('useOrganization must be used within a OrganizationProvider');
  }

  return context;
}
